import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calculator, Filter, Plus, Search, X } from "lucide-react";

import { useCreateProjectEstimate, useProjectEstimates } from "@/api/queries";
import type { ProjectEstimate, ProjectEstimateStatus } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, formatProjectEstimateStatus, pluralize } from "@/lib/format";
import { projectEstimateTone } from "@/lib/stock";

const PAGE_SIZE = 50;

const STATUSES: ProjectEstimateStatus[] = [
  "DRAFT",
  "ACTIVE",
  "ACCEPTED",
  "REJECTED",
  "ARCHIVED",
];

/**
 * The estimates (V1.6-01).
 *
 * An estimate forecasts and engages nothing: no reservation, no purchase, no
 * ledger write. What it does carry is history — the price quoted six weeks ago
 * is still readable, exactly as it was quoted.
 */
export function ProjectEstimatesPage() {
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(term);
  const [creating, setCreating] = useState(false);

  // Ouvrir la création depuis un lien — le dashboard s'en sert. Le paramètre
  // est consommé tout de suite pour que revenir en arrière ne rouvre pas la
  // boîte, et pour qu'un rafraîchissement ne la rouvre pas non plus.
  useEffect(() => {
    if (params.get("new") === null) return;
    setCreating(true);
    const merged = new URLSearchParams(params);
    merged.delete("new");
    setParams(merged, { replace: true });
  }, [params, setParams]);


  const status = params.get("status") ?? undefined;
  const offset = Number(params.get("offset") ?? 0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [term]);

  const query = useMemo(
    () => ({
      search: debounced || undefined,
      status: status as ProjectEstimateStatus | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [debounced, status, offset],
  );

  const { data, isLoading, error, refetch } = useProjectEstimates(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  const hasFilters = Boolean(debounced || status);

  return (
    <Page>
      <PageHeader
        title="Estimations"
        subtitle="Un chiffrage, ses versions, et le prix qu'on a donné — tel qu'on l'a donné."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            Nouvelle estimation
          </Button>
        }
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                update({ q: event.target.value, offset: undefined });
              }}
              placeholder="Nom, code…"
              className="pl-9"
              aria-label="Rechercher une estimation"
            />
          </div>

          <Select
            value={status ?? ""}
            onChange={(event) => update({ status: event.target.value || undefined })}
            aria-label="Filtrer par statut"
            className="sm:w-52"
          >
            <option value="">Statuts actifs</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {formatProjectEstimateStatus(value)}
              </option>
            ))}
          </Select>

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setTerm("");
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={5} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                hasFilters ? (
                  <Filter className="size-6" aria-hidden />
                ) : (
                  <Calculator className="size-6" aria-hidden />
                )
              }
              title={hasFilters ? "Aucune estimation ne correspond" : "Aucune estimation"}
              description={
                hasFilters
                  ? "Essayez un autre terme, ou retirez les filtres."
                  : "Une estimation chiffre un travail avant qu'il existe. Elle ne réserve rien et n'achète rien."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-32 whitespace-nowrap">Code</Th>
                      <Th className="max-w-0">Estimation</Th>
                      <Th className="hidden whitespace-nowrap @4xl:table-cell">Créée</Th>
                      <Th align="right" className="whitespace-nowrap">
                        Version
                      </Th>
                      <Th>Statut</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <EstimateRow key={item.id} item={item} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <EstimateCard item={item} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>

      <NewEstimateDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

/** "V3 / 3 versions" — the current one, and how many came before it. */
function versionLabel(item: ProjectEstimate): string {
  if (item.current_version_number === null) return "—";
  return `V${item.current_version_number}`;
}

function EstimateRow({ item }: { item: ProjectEstimate }) {
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/estimates/${item.id}`} className="hover:text-brand">
          {item.code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link to={`/estimates/${item.id}`} className="block min-w-0 hover:text-brand">
          <span className="block truncate font-medium text-ink">{item.name}</span>
          {item.description && (
            <span className="block truncate text-[11px] text-ink-subtle">{item.description}</span>
          )}
        </Link>
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @4xl:table-cell">
        {formatDate(item.created_at)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {versionLabel(item)}
      </Td>
      <Td>
        <StatusBadge tone={projectEstimateTone(item.status)}>
          {formatProjectEstimateStatus(item.status)}
        </StatusBadge>
      </Td>
    </Tr>
  );
}

function EstimateCard({ item }: { item: ProjectEstimate }) {
  return (
    <Link
      to={`/estimates/${item.id}`}
      className="flex min-h-[64px] items-center gap-3 px-3 py-3 active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{item.name}</p>
        <p className="truncate font-mono text-[11px] text-ink-subtle">{item.code}</p>
        <p className="mt-0.5 truncate text-[12px] text-ink-muted">
          {versionLabel(item)} · {pluralize(item.version_count, "version")}
        </p>
      </div>
      <StatusBadge tone={projectEstimateTone(item.status)} className="shrink-0">
        {formatProjectEstimateStatus(item.status)}
      </StatusBadge>
    </Link>
  );
}

function NewEstimateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const createEstimate = useCreateProjectEstimate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  function reset() {
    setName("");
    setDescription("");
  }

  async function submit() {
    if (!name.trim()) return;
    try {
      const created = await createEstimate.mutateAsync({ name, description });
      toast.success("Estimation créée", `${created.code} — V1 prête à être remplie`);
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Estimation non créée", String(error));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Nouvelle estimation"
      description="V1 est créée avec elle, vide et modifiable."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!name.trim() || createEstimate.isPending}
          >
            {createEstimate.isPending ? "Création…" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Nom de l'estimation">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Chiffrage garage"
            aria-label="Nom de l'estimation"
          />
        </Field>
        <Field label="Description" hint="Facultatif.">
          <Input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            aria-label="Description de l'estimation"
          />
        </Field>
      </div>
    </Dialog>
  );
}
